import { useRef, useState } from 'react';
import type { HoldList } from 'api-client/models';
import type { useBulkOperations } from './useBulkOperations';

type CopyExpenses = ReturnType<typeof useBulkOperations>['copyExpensesToAllDates'];
interface ExpenseCopyDialogOptions {
  showId?: string;
  selectedHold: HoldList | null;
  holds: HoldList[];
  formData: Parameters<CopyExpenses>[2];
  fetchDetails: Parameters<CopyExpenses>[3];
  copyExpenses: CopyExpenses;
  refetch: () => Promise<void>;
  setError: (message: string | null) => void;
}

export const useExpenseCopyDialog = ({
  showId, selectedHold, holds, formData, fetchDetails, copyExpenses, refetch, setError,
}: ExpenseCopyDialogOptions) => {
  const session = useRef({ showId, holdId: selectedHold?.id });
  if (session.current.showId !== showId || session.current.holdId !== selectedHold?.id) {
    session.current = { showId, holdId: selectedHold?.id };
  }
  const version = session.current;
  const [state, setState] = useState<{
    version: typeof version; open: boolean; pendingIds: Set<string> | null; error: string | null; uncertain: boolean;
  }>({ version, open: false, pendingIds: null, error: null, uncertain: false });
  const current = state.version === version
    ? state : { version, open: false, pendingIds: null, error: null, uncertain: false };

  const onConfirm = async (): Promise<void> => {
    if (session.current !== version || !selectedHold) return;
    if (current.pendingIds?.size === 0) {
      try {
        await refetch();
        if (session.current === version) {
          setError(current.uncertain
            ? 'Some copy request outcomes are unknown. Inspect the refreshed dates before copying again.' : null);
          setState({ ...current, open: false, error: null });
        }
      } catch {
        if (session.current === version) {
          setState({ ...current, error: 'Could not refresh dates. Copied expenses will not be appended again.' });
        }
      }
      return;
    }
    const result = await copyExpenses(
      selectedHold,
      current.pendingIds ? holds.filter((hold) => hold.id === selectedHold.id || current.pendingIds?.has(hold.id)) : holds,
      formData,
      fetchDetails,
    );
    if (result.cancelled || session.current !== version) return;
    const uncertain = current.uncertain || result.uncertainHoldIds.length > 0;
    setState({
      version, open: !result.success || uncertain, error: result.error || current.error, uncertain,
      pendingIds: result.copiedHoldIds.length || result.failedHoldIds.length
        ? new Set(result.failedHoldIds.filter((holdId) => !result.uncertainHoldIds.includes(holdId))) : current.pendingIds,
    });
  };

  return {
    open: current.open,
    errorMessage: current.error,
    refreshOnly: current.pendingIds?.size === 0,
    targetDateCount: current.pendingIds?.size ?? (selectedHold ? holds.filter((hold) =>
      hold.status === 'confirmed' && hold.id !== selectedHold.id).length : 0),
    onOpen: async (): Promise<void> => {
      setState({ version, open: true, pendingIds: null, error: null, uncertain: false });
    },
    onClose: () => setState({ ...current, open: false }),
    onConfirm,
  };
};
